import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { DISPLAY_NAMES } from "../constants";
import type { HotKeyProps, KeyModifier } from "../types";
import type { HotkeyConfig, NormalizedKeyEvent } from "capitalrx-hotkeys";
import { useHotkeyContext } from "./HotkeyProvider";

export function HotKey({
  combo,
  title,
  description,
  onTrigger,
  priority = 50,
  scope = "global",
  category,
  disabled = false,
  preventDefault = true,
  stopPropagation = false,
  // element is accepted for API compatibility but not used directly
  element: _element,
  allowInInput = false,
}: HotKeyProps): ReactNode {
  const { manager, enabled } = useHotkeyContext();
  const hotkeyIdRef = useRef<string | null>(null);
  const onTriggerRef = useRef(onTrigger);

  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    if (!enabled || disabled) {
      if (hotkeyIdRef.current) {
        manager.unregister?.(hotkeyIdRef.current);
        hotkeyIdRef.current = null;
      }
      return;
    }

    const parts = combo
      .split("+")
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
    const key = parts[parts.length - 1];
    const modifiers = parts.slice(0, -1) as KeyModifier[];

    const hotkeyConfig: HotkeyConfig = {
      key,
      modifiers,
      scope,
      description: description || title,
      handler: (event: NormalizedKeyEvent) => {
        onTriggerRef.current(event.originalEvent);
      },
      enabled: true,
      allowWhenInputFocused: allowInInput,
      preventDefault,
      stopPropagation,
      category,
      priority,
    };

    const id = manager.register?.(hotkeyConfig);
    if (id) {
      hotkeyIdRef.current = id;
    }

    return () => {
      if (hotkeyIdRef.current) {
        manager.unregister?.(hotkeyIdRef.current);
        hotkeyIdRef.current = null;
      }
    };
  }, [
    manager,
    combo,
    title,
    description,
    priority,
    scope,
    category,
    enabled,
    disabled,
    preventDefault,
    stopPropagation,
    allowInInput,
  ]);

  return null;
}

HotKey.displayName = DISPLAY_NAMES.HotKey;
